import React from 'react';
import { Plus, Trash2, ArrowUp, ArrowDown, Copy, GripVertical } from 'lucide-react';

const STATE_DOT = (s) =>
  s === 0 ? 'bg-white/10' : s === 2 ? 'bg-rose-400' : 'bg-pink-400/70';

export default function SongPanel({
  sections,
  selectedIndex,
  currentSection,
  isPlaying,
  loopSong,
  onSelectSection,
  onAddSection,
  onDuplicateSection,
  onDeleteSection,
  onMoveSection,
  onUpdateSection,
  onToggleLoop,
}) {
  return (
    <section
      data-testid="song-panel"
      className="bg-[#111112] border border-white/5 rounded-2xl p-6 md:p-8 shadow-xl"
    >
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-white/50">
            Modalità Song · sequenza di sezioni
          </div>
          <div className="font-bebas text-2xl tracking-wide">
            {sections.length} {sections.length === 1 ? 'sezione' : 'sezioni'}
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-xs uppercase tracking-widest text-white/60 cursor-pointer">
            <input
              data-testid="loop-song-checkbox"
              type="checkbox"
              checked={loopSong}
              onChange={(e) => onToggleLoop(e.target.checked)}
              className="accent-pink-400 w-4 h-4"
            />
            Loop
          </label>
          <button
            data-testid="add-section-btn"
            onClick={onAddSection}
            className="h-10 px-4 rounded-md bg-pink-400 hover:bg-pink-300 text-black font-medium text-sm flex items-center gap-2 active:scale-95 transition-all"
          >
            <Plus className="w-4 h-4" /> Aggiungi sezione
          </button>
        </div>
      </div>

      {sections.length === 0 ? (
        <div className="text-sm text-white/40 italic py-10 text-center border border-dashed border-white/10 rounded-md">
          Nessuna sezione. Premi <span className="text-pink-400">Aggiungi sezione</span> per iniziare.
          La configurazione corrente (BPM, tempo, pattern, battute) verrà salvata come prima sezione.
        </div>
      ) : (
        <ul data-testid="sections-list" className="flex flex-col gap-2">
          {sections.map((sec, i) => {
            const isSelected = i === selectedIndex;
            const isCurrent = isPlaying && i === currentSection;
            return (
              <li
                key={sec.id}
                data-testid={`section-item-${i}`}
                onClick={() => onSelectSection(i)}
                className={`group rounded-md p-3 cursor-pointer border transition-all flex items-center gap-3 ${
                  isCurrent
                    ? 'bg-pink-500/10 border-pink-400/60 shadow-[0_0_20px_rgba(236,72,153,0.2)]'
                    : isSelected
                      ? 'bg-[#0A0A0A] border-pink-400/40'
                      : 'bg-[#0A0A0A] border-white/5 hover:border-white/20'
                }`}
              >
                <div className="flex items-center gap-2 text-white/30">
                  <GripVertical className="w-4 h-4" />
                  <span className="font-bebas text-2xl tabular-nums w-7 text-right">
                    {i + 1}
                  </span>
                </div>

                <input
                  data-testid={`section-name-input-${i}`}
                  type="text"
                  value={sec.name}
                  onChange={(e) => onUpdateSection(i, { name: e.target.value })}
                  onClick={(e) => e.stopPropagation()}
                  className="bg-transparent border-0 outline-none font-bebas text-xl tracking-wide text-white w-32 sm:w-40 truncate focus:text-pink-300"
                />

                <div className="flex items-center gap-3 text-xs text-white/50 font-mono ml-auto">
                  <span><span className="text-white/80 font-bold tabular-nums">{sec.bpm}</span> bpm</span>
                  <span>·</span>
                  <span>{sec.signature.toUpperCase()}</span>
                  <span>·</span>
                  <span>
                    <input
                      data-testid={`section-bars-input-${i}`}
                      type="number"
                      min={1}
                      max={1000}
                      value={sec.numBars}
                      onChange={(e) =>
                        onUpdateSection(i, {
                          numBars: Math.max(1, Math.min(1000, parseInt(e.target.value, 10) || 1)),
                        })
                      }
                      onClick={(e) => e.stopPropagation()}
                      className="bg-transparent border border-white/10 rounded w-14 px-1.5 py-0.5 text-center text-white"
                    />{' '}
                    bar
                  </span>
                </div>

                <div className="hidden md:flex items-center gap-0.5 ml-2 max-w-[120px]">
                  {sec.pattern.slice(0, 16).map((s, idx) => (
                    <span key={idx} className={`w-1 h-4 rounded-sm ${STATE_DOT(s)}`} />
                  ))}
                </div>

                <div className="flex items-center gap-1 ml-2">
                  <button
                    data-testid={`section-up-btn-${i}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onMoveSection(i, -1);
                    }}
                    disabled={i === 0}
                    className="p-1.5 rounded hover:bg-white/5 disabled:opacity-20 disabled:hover:bg-transparent transition-all"
                    aria-label="Sposta su"
                  >
                    <ArrowUp className="w-3.5 h-3.5" />
                  </button>
                  <button
                    data-testid={`section-down-btn-${i}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onMoveSection(i, 1);
                    }}
                    disabled={i === sections.length - 1}
                    className="p-1.5 rounded hover:bg-white/5 disabled:opacity-20 disabled:hover:bg-transparent transition-all"
                    aria-label="Sposta giù"
                  >
                    <ArrowDown className="w-3.5 h-3.5" />
                  </button>
                  <button
                    data-testid={`section-duplicate-btn-${i}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDuplicateSection(i);
                    }}
                    className="p-1.5 rounded hover:bg-white/5 transition-all"
                    aria-label="Duplica"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                  <button
                    data-testid={`section-delete-btn-${i}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteSection(i);
                    }}
                    className="p-1.5 rounded hover:bg-rose-500/20 hover:text-rose-300 transition-all"
                    aria-label="Elimina"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {sections.length > 0 && selectedIndex >= 0 && (
        <p className="text-xs text-white/40 mt-4 leading-relaxed">
          Modifica i pannelli sopra (BPM, Tempo, Pattern) per aggiornare la sezione selezionata
          <span className="text-pink-300"> "{sections[selectedIndex]?.name}"</span>.
        </p>
      )}
    </section>
  );
}
